import React from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";

/**
 * useUrlSearchState — `useState`-shaped hook that mirrors a single
 * query-string key in the current URL. Lets operators bookmark or
 * share filtered views (`/agents?q=ny-prod`) and survives reloads
 * without needing extra glue in every page.
 *
 * Implementation notes:
 *   - We delete the param when the value is empty so the URL stays
 *     clean (no trailing `?q=`).
 *   - `replace: true` so each keystroke doesn't pollute the browser
 *     history stack — the back button still goes to the previous page.
 *   - Other unrelated query params (like `?source=…` deep-links into
 *     the capture dialog) are preserved as-is.
 *
 * Usage:
 *   const [q, setQ] = useUrlSearchState("q");
 *   <SearchInput value={q} onChange={setQ} ... />
 */
export function useUrlSearchState(paramKey = "q", defaultValue = "") {
    const [params, setParams] = useSearchParams();
    const value = params.get(paramKey) ?? defaultValue;
    const setValue = React.useCallback(
        (next) => {
            setParams(
                (prev) => {
                    const sp = new URLSearchParams(prev);
                    if (!next) sp.delete(paramKey);
                    else sp.set(paramKey, next);
                    return sp;
                },
                { replace: true },
            );
        },
        [paramKey, setParams],
    );
    return [value, setValue];
}

/**
 * SearchInput — themed search box used by the device + migration list
 * pages (Devices, Server Migration, Data Migration, New PC
 * Migration capture picker).
 *
 * Light/dark aware: uses `bg-card`, `border-border`, `text-foreground`,
 * `text-muted-foreground` so the box renders correctly under the
 * light-mode override block in `index.css`. Clear button appears once
 * the query is non-empty.
 */
export default function SearchInput({
    value,
    onChange,
    placeholder = "Search by name…",
    testid = "search-input",
    className = "",
    autoFocus = false,
}) {
    return (
        <div
            className={`relative flex items-center ${className}`}
            data-testid={`${testid}-wrap`}
        >
            <Search
                size={14}
                className="pointer-events-none absolute left-3 text-muted-foreground"
                aria-hidden
            />
            <input
                type="search"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoFocus={autoFocus}
                data-testid={testid}
                spellCheck={false}
                className="w-full rounded-sm border border-border bg-card pl-9 pr-9 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-[#00C2FF]/60 focus:ring-2 focus:ring-[#00C2FF]/15"
            />
            {value && (
                <button
                    type="button"
                    onClick={() => onChange("")}
                    aria-label="Clear search"
                    data-testid={`${testid}-clear`}
                    className="absolute right-2 inline-flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground transition hover:bg-muted hover:text-foreground"
                >
                    <X size={12} />
                </button>
            )}
        </div>
    );
}

/**
 * Filter a list of objects by a free-text query against any
 * combination of fields. Case-insensitive, treats empty query as a
 * no-op (returns the original list). Safe against missing/null
 * fields. Use across every list page that adopts SearchInput.
 *
 * Example:
 *   const visible = filterByName(agents, q, ["name", "hostname", "id"]);
 */
export function filterByName(list, query, fields = ["name", "hostname"]) {
    const q = (query || "").trim().toLowerCase();
    if (!q) return list || [];
    return (list || []).filter((item) => {
        if (!item) return false;
        for (const f of fields) {
            const v = item[f];
            if (typeof v === "string" && v.toLowerCase().includes(q)) {
                return true;
            }
        }
        return false;
    });
}
